import { Link, useParams } from 'react-router-dom';
import { NavigationProps } from '../constants';
import { useTranslation } from 'react-i18next';
import { genLink } from '../utils';
import { APP_DOMAINS } from '~/types';
import { Logout } from '~/components/shared/Logout';
import { IconButton } from './IconButton';
import { ProfileImage } from '~/components/shared/ProfileImage';
import { MessageCounter } from './MessageCounter';
import { ChangeTheme } from '~/components/shared/Theme';
import { AgentSelector } from '~/components/shared/AgentSelector';
import { useSession } from '~/components/contexts/SessionProvider';
import { isAdmin } from '~/lib/admin';
import { ChartBarIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { useSubscription } from '~/components/contexts/SubscriptionProvider';
import { FeatureUpsellModal } from '~/components/shared/FeatureUpsellModal';

export const DesktopNavigation = ({
  appDomain,
  setAppDomain,
  routes,
}: NavigationProps) => {
  const { t } = useTranslation('navigation');
  const { agentId } = useParams();
  const { owner } = useSession();
  const { hasPaidSubscription, features } = useSubscription();

  return (
    <div
      className="bg-base-100 flex h-full shadow-lg"
      onMouseLeave={() => setAppDomain(null)}
    >
      <div className="bg-base-100 flex h-screen w-28 flex-col items-center justify-between pt-8">
        <div className="border-base-300 relative h-[65%] w-full overflow-y-scroll border-b">
          <div className="flex h-full flex-col items-center gap-6 overflow-y-scroll px-3 pb-6 pt-2">
            {agentId && <MessageCounter agentId={agentId} />}
            {/* Display all available routes */}
            {Object.values(routes).map((route) => (
              <div key={route.label}>
                <Link
                  to={genLink({
                    domain: route.label as APP_DOMAINS,
                    agentIdParams: agentId!,
                    subRoute: route.routes![0].route,
                  })}
                  onClick={(e) => {
                    const domain = route.label as APP_DOMAINS;
                    const campaignsEnabled = features?.campaigns === true;
                    const calendarEnabled = features?.calendar === true;
                    const isCampaignsBlocked =
                      domain === APP_DOMAINS.CAMPAIGNS && !campaignsEnabled;
                    const isCalendarBlocked =
                      domain === APP_DOMAINS.SCHEDULE && !calendarEnabled;
                    if (isCampaignsBlocked || isCalendarBlocked) {
                      e.preventDefault();
                      const id = isCampaignsBlocked
                        ? 'upsell_campaigns'
                        : 'upsell_calendar';
                      const dialog = document.getElementById(
                        id,
                      ) as HTMLDialogElement | null;
                      dialog?.showModal();
                      return;
                    }
                  }}
                >
                  <IconButton
                    setAppDomain={() =>
                      setAppDomain(route.label as APP_DOMAINS)
                    }
                    {...route}
                  />
                </Link>
              </div>
            ))}
          </div>
          {/* Gradient overlay stays fixed at bottom of this section */}
          <div className="from-base-200 pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t to-transparent" />
        </div>
        {/* Bottom fixed section */}
        <div className="flex w-full flex-col items-center gap-5 px-3 pb-4 pt-4">
          <AgentSelector />
          {/* Admin Analytics Button - only show for admin users */}
          {isAdmin(owner.email) && (
            <Link
              to="/admin/analytics"
              className="hover:bg-custom-info-100 focus:bg-custom-info-100 flex w-full cursor-pointer flex-col items-center gap-1 rounded-md p-1 transition-all duration-150 hover:scale-110 hover:shadow-sm"
              title={t('admin_analytics')}
            >
              <ChartBarIcon className="h-5 w-5 text-current" />
              <label className="text-[9px]">{t('menu_admin')}</label>
            </Link>
          )}
          {/* Referrals Button - only show for owners with referrals enabled */}
          {owner.allow_referral && (
            <Link
              to="/referrals"
              className="hover:bg-custom-info-100 focus:bg-custom-info-100 flex w-full cursor-pointer flex-col items-center gap-1 rounded-md p-1 transition-all duration-150 hover:scale-110 hover:shadow-sm"
              title={t('referrals')}
            >
              <CurrencyDollarIcon className="h-5 w-5 text-current" />
              <label className="text-[9px]">{t('menu_referrals')}</label>
            </Link>
          )}
          <ChangeTheme />
          <ProfileImage />
          <Logout />
        </div>
      </div>

      {appDomain &&
        routes[appDomain] &&
        routes[appDomain].routes!.length > 1 && (
          <div className="bg-base-100 flex h-full w-60 p-4">
            <div className="flex w-full flex-col gap-4 overflow-y-scroll">
              <h2 className="text-lg font-semibold">
                {t(routes[appDomain].label)}
              </h2>
              {(routes[appDomain].routes &&
                routes[appDomain]?.routes?.length) ||
              0 > 0 ? (
                routes[appDomain]?.routes?.map((subRoute) => (
                  <Link
                    key={subRoute.route}
                    to={genLink({
                      domain: appDomain,
                      agentIdParams: agentId!,
                      subRoute: subRoute.route,
                    })}
                    className={`text-sm ${
                      agentId === subRoute.route
                        ? 'text-primary font-bold'
                        : 'text-base-content hover:text-primary'
                    }`}
                    onClick={() => setAppDomain(null)}
                  >
                    {t(subRoute.label)}
                  </Link>
                ))
              ) : (
                <p className="text-base-content text-sm opacity-70">
                  No sub-routes available.
                </p>
              )}
            </div>
          </div>
        )}
      <FeatureUpsellModal id="upsell_campaigns" feature="campaigns" />
      <FeatureUpsellModal id="upsell_calendar" feature="calendar" />
    </div>
  );
};
